import React, { useState, useMemo } from "react";

// Bootstrap contextual styling
const statusClasses = {
  available: "bg-success text-white",
  held: "bg-warning text-dark",
  sold: "bg-danger text-white",
};

export default function MasterTicketMatrixSticky({ matrix }) {
  const [filter, setFilter] = useState("all"); // all | sold | multi

  // Flatten structured matrix
  const processed = useMemo(() => {
    const flattened = [];

    for (const [section, rows] of Object.entries(matrix)) {
      for (const [rowNumber, seatArray] of Object.entries(rows)) {
        const grouped = seatArray.reduce((acc, item) => {
          if (!acc[item.seat]) acc[item.seat] = [];
          acc[item.seat].push(item);
          return acc;
        }, {});

        flattened.push({
          section,
          row: rowNumber,
          groupedSeats: grouped,
        });
      }
    }

    return flattened;
  }, [matrix]);

  // Collect seat numbers for columns
  const allSeatNumbers = useMemo(() => {
    const seats = new Set();
    processed.forEach((r) => {
      Object.keys(r.groupedSeats).forEach((seat) => seats.add(seat));
    });
    return Array.from(seats).sort((a, b) => Number(a) - Number(b));
  }, [processed]);

  // Filtering logic
  const filteredRows = useMemo(() => {
    if (filter === "all") return processed;

    return processed.filter((row) => {
      const seats = Object.values(row.groupedSeats);

      if (filter === "sold") {
        return seats.some((events) => events.some((e) => e.status === "sold"));
      }

      if (filter === "multi") {
        return seats.some((events) => events.length > 1);
      }

      return true;
    });
  }, [processed, filter]);

  return (
    <div className="my-4">
      {/* FILTER BUTTONS */}
      <div className="d-flex gap-2 mb-3">
        <button
          className={`btn btn-sm ${filter === "all" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setFilter("all")}
        >
          Show All
        </button>

        <button
          className={`btn btn-sm ${filter === "sold" ? "btn-danger" : "btn-outline-danger"}`}
          onClick={() => setFilter("sold")}
        >
          Show Only Sold
        </button>

        <button
          className={`btn btn-sm ${filter === "multi" ? "btn-warning" : "btn-outline-warning"}`}
          onClick={() => setFilter("multi")}
        >
          Multi-Event Seats Only
        </button>
      </div>

      {/* WRAPPER for sticky + scroll */}
      <div
        className="table-responsive"
        style={{ maxHeight: "70vh", overflow: "auto" }}
      >
        <table
          className="table table-bordered table-striped align-middle"
          style={{ margin: 0 }}
        >
          <thead
            className="table-light"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 5,
            }}
          >
            <tr>
              <th
                style={{
                  position: "sticky",
                  left: 0,
                  zIndex: 6,
                  background: "#f8f9fa",
                }}
              >
                Section
              </th>

              <th
                style={{
                  position: "sticky",
                  left: "100px",
                  zIndex: 6,
                  background: "#f8f9fa",
                }}
              >
                Row
              </th>

              {allSeatNumbers.map((seat) => (
                <th key={seat}>{seat}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td
                  colSpan={allSeatNumbers.length + 2}
                  className="text-center text-muted"
                >
                  No data matches the selected filter.
                </td>
              </tr>
            ) : (
              filteredRows.map((row, index) => (
                <tr key={index}>
                  {/* Sticky Section Column */}
                  <td
                    className="fw-bold table-light"
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 4,
                      background: "#f8f9fa",
                    }}
                  >
                    {row.section}
                  </td>

                  {/* Sticky Row Column */}
                  <td
                    className="fw-bold table-light"
                    style={{
                      position: "sticky",
                      left: "100px",
                      zIndex: 4,
                      background: "#f8f9fa",
                    }}
                  >
                    {row.row}
                  </td>

                  {allSeatNumbers.map((seat) => {
                    const events = row.groupedSeats[seat] || [];

                    return (
                      <td key={seat}>
                        {events.length === 0 ? (
                          <span className="text-muted">N/A</span>
                        ) : (
                          <div className="d-flex flex-column gap-1">
                            {events.map((entry, idx) => {
                              const colorClass =
                                statusClasses[entry.status] ||
                                "bg-secondary text-white";

                              return (
                                <div
                                  key={idx}
                                  className={`p-2 rounded ${colorClass}`}
                                >
                                  <div className="fw-semibold text-capitalize">
                                    {entry.status}
                                  </div>
                                  {/* <div className="small">{entry.event}</div>
                                  <div className="small text-light fst-italic">
                                    {new Date(entry.date).toLocaleString()}
                                  </div> */}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
